/**
 * GSTR-3B · full form with clickable line items.
 * Auto-populates Table 3.1 from filed GSTR-1 (or current-month sales register
 * if 1 isn't filed yet), Table 4A(1-5), Table 4B reversals (Rule 42 + Rule 37
 * + 17(5)), Table 4D details.
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { RefreshCw, Send, CheckCircle2, Calculator, FileCheck } from "lucide-react";

const KPI = ({ label, value, tone = "text-white/90" }) => (
  <div className="card-sharp p-3">
    <div className="text-[10px] font-mono uppercase text-muted-foreground tracking-widest">{label}</div>
    <div className={`text-lg font-bold mt-1 font-mono tabular-nums ${tone}`}>{value}</div>
  </div>
);

export default function Gstr3bForm() {
  const now = new Date();
  const [period, setPeriod] = useState(
    String(now.getMonth() + 1).padStart(2, "0") + now.getFullYear()
  );
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [markingFiled, setMarkingFiled] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/gstr3b/preview/${period}`);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const push = async () => {
    if (!window.confirm(`Push GSTR-3B for ${period} to Sandbox / GST portal?`)) return;
    setPushing(true);
    try {
      const r = await api.post(`/gstr3b/push/${period}`);
      toast.success("Pushed to portal");
      console.log(r.data);
    } catch (e) {
      const d = e?.response?.data?.detail || "Push failed";
      if (String(d).toLowerCase().includes("insufficient")) {
        toast.error("Sandbox 'GST · Filing' scope not unlocked yet. Email sales@sandbox.co.in.", { duration: 8000 });
      } else toast.error(d);
    }
    setPushing(false);
  };

  const markFiled = async () => {
    const arn = window.prompt("ARN from GST portal filing:");
    if (!arn) return;
    setMarkingFiled(true);
    try {
      const r = await api.post(`/gstr3b/mark-filed/${period}`, {
        arn,
        verified_via: "evc",
        offset: {
          output_cgst: data?.table_3_1?.a_outward_taxable?.cgst || 0,
          output_sgst: data?.table_3_1?.a_outward_taxable?.sgst || 0,
          output_igst: data?.table_3_1?.a_outward_taxable?.igst || 0,
          itc_utilised: data?.table_4?.c_net_itc || 0,
          cash_paid: 0, interest: 0, late_fee: 0,
        },
      });
      toast.success(`Filed! Draft JV created in 'pending_review' with ${r.data.jv_lines.length} lines. Ask your accountant to review.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Mark-filed failed");
    }
    setMarkingFiled(false);
  };

  const inr = (n) => "₹ " + Number(n || 0).toLocaleString("en-IN",
    { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  if (!data) {
    return <div className="p-6 text-muted-foreground">Loading GSTR-3B…</div>;
  }
  const t31 = data.table_3_1;
  const t4 = data.table_4;
  const t4a5 = t4.a_eligible_itc.a5_all_other;
  const t4b1 = t4.b_reversal.b1_permanent;

  return (
    <div className="p-6 lg:p-8 space-y-4" data-testid="gstr3b-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-accent">// GST · GSTR-3B</div>
          <h1 className="text-3xl font-bold mt-1 font-heading">GSTR-3B · Monthly Return</h1>
          <div className="text-sm text-muted-foreground mt-1 max-w-3xl">
            Table 3.1 auto-populated from filed GSTR-1 (or current-month sales register if 1 not filed yet).
            Table 4 includes Rule 42 common-ITC reversal, Rule 37 (180-day) reversals, and 17(5) blocked credit.
            After filing on the portal, mark as filed here to create the accountant-review JV.
          </div>
        </div>
        <div className="flex gap-2 items-center">
          <Input value={period} onChange={(e) => setPeriod(e.target.value)}
            placeholder="MMYYYY" className="w-32 rounded-sm h-9 font-mono" data-testid="gstr3b-period" />
          <Button onClick={load} disabled={loading} variant="outline"
            className="rounded-sm gap-1.5" data-testid="gstr3b-refresh">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Reload
          </Button>
          <Button onClick={push} disabled={pushing}
            className="rounded-sm bg-indigo-600 hover:bg-indigo-700 text-white gap-1.5"
            data-testid="gstr3b-push">
            {pushing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Push
          </Button>
          <Button onClick={markFiled} disabled={markingFiled}
            className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5"
            data-testid="gstr3b-mark-filed">
            {markingFiled ? <RefreshCw className="w-4 h-4 animate-spin" /> : <FileCheck className="w-4 h-4" />} Mark filed → Draft JV
          </Button>
        </div>
      </div>

      {/* Source banner */}
      <div className={`border-l-4 px-4 py-2 rounded-sm text-xs ${
        t31.sourced_from === "gstr1_filed" ?
          "border-emerald-500 bg-emerald-500/10 text-emerald-400" :
          "border-amber-500 bg-amber-500/10 text-amber-400"}`}>
        {t31.sourced_from === "gstr1_filed" ?
          `Table 3.1 auto-populated from FILED GSTR-1 (ARN ${t31.gstr1_arn}).` :
          "Table 3.1 sourced from CURRENT sales register — GSTR-1 not yet filed for this period."}
      </div>

      {/* Table 3.1 · Outward */}
      <div className="card-sharp p-4 space-y-2" data-testid="table-3-1">
        <div className="text-sm font-mono uppercase tracking-widest text-accent">Table 3.1 · Outward supplies</div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          <KPI label="3.1(a) Taxable · value" value={inr(t31.a_outward_taxable.taxable_value)} tone="text-emerald-500" />
          <KPI label="3.1(a) CGST" value={inr(t31.a_outward_taxable.cgst)} />
          <KPI label="3.1(a) SGST" value={inr(t31.a_outward_taxable.sgst)} />
          <KPI label="3.1(a) IGST" value={inr(t31.a_outward_taxable.igst)} />
          <KPI label="3.1(a) Cess" value={inr(t31.a_outward_taxable.cess)} />
          <KPI label="3.1(b) Zero-rated · value" value={inr(t31.b_outward_zero_rated.taxable_value)} tone="text-sky-500" />
          <KPI label="3.1(b) IGST" value={inr(t31.b_outward_zero_rated.igst)} />
          <KPI label="3.1(c) Nil/Exempt" value={inr(t31.c_other_outward_nil.taxable_value)} tone="text-white/70" />
          <KPI label="3.1(e) Non-GST" value={inr(t31.e_non_gst_outward.taxable_value)} tone="text-white/70" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 border-t border-white/10 pt-2 mt-2">
          <KPI label="3.1(d) Inward RCM · value" value={inr(t31.d_inward_rcm.taxable_value)} tone="text-fuchsia-500" />
          <KPI label="3.1(d) IGST" value={inr(t31.d_inward_rcm.igst)} tone="text-fuchsia-500" />
          <KPI label="3.1(d) CGST" value={inr(t31.d_inward_rcm.cgst)} tone="text-fuchsia-500" />
          <KPI label="3.1(d) SGST" value={inr(t31.d_inward_rcm.sgst)} tone="text-fuchsia-500" />
        </div>
      </div>

      {/* Table 4A · Eligible ITC */}
      <div className="card-sharp p-4 space-y-2" data-testid="table-4a">
        <div className="text-sm font-mono uppercase tracking-widest text-accent">Table 4A · Eligible ITC</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <KPI label="4A(1) Import goods IGST" value={inr(t4.a_eligible_itc.a1_import_goods.igst)} />
          <KPI label="4A(2) Import svc RCM IGST" value={inr(t4.a_eligible_itc.a2_import_services_rcm.igst)} />
          <KPI label="4A(3) Regular RCM · total"
            value={inr(t4.a_eligible_itc.a3_regular_rcm.igst + t4.a_eligible_itc.a3_regular_rcm.cgst + t4.a_eligible_itc.a3_regular_rcm.sgst)} />
          <KPI label="4A(4) ISD · total"
            value={inr(t4.a_eligible_itc.a4_isd.igst + t4.a_eligible_itc.a4_isd.cgst + t4.a_eligible_itc.a4_isd.sgst)} />
        </div>
        <div className="mt-2 border-t border-white/10 pt-2">
          <div className="text-[11px] font-mono uppercase text-muted-foreground tracking-widest mb-1">4A(5) All other ITC</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <KPI label="I · Current month 2B" value={inr(t4a5.i_current_month_2b.igst + t4a5.i_current_month_2b.cgst + t4a5.i_current_month_2b.sgst)} />
            <KPI label="III · Rule 37 reclaim" value={inr(t4a5.iii_rule_37_reclaim)} />
            <KPI label="Total 4A(5)" value={inr(t4a5.total)} tone="text-emerald-500" />
            <KPI label="2B rows" value={t4a5.i_current_month_2b.row_count} />
          </div>
        </div>
      </div>

      {/* Table 4B · Reversals */}
      <div className="card-sharp p-4 space-y-2" data-testid="table-4b">
        <div className="text-sm font-mono uppercase tracking-widest text-accent">Table 4B · ITC Reversals</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <KPI label="4B(1)(I) 17(5) Ineligible" value={inr(t4b1.i_ineligible_17_5)} tone="text-amber-500" />
          <KPI label="4B(1)(II) Rule 42 · Total" value={inr(t4b1.ii_rule_42.total_reversal)} tone="text-amber-500" />
          <KPI label="4B(1)(III) Rule 37" value={inr(t4b1.iii_rule_37)} tone="text-amber-500" />
          <KPI label="Total 4B(1)" value={inr(t4b1.total)} tone="text-red-500" />
        </div>
        {/* Rule 42 breakdown */}
        <div className="border border-white/10 rounded-sm p-2 text-xs bg-black/20 space-y-1 mt-2" data-testid="rule42-detail">
          <div className="text-[10px] font-mono uppercase tracking-widest text-amber-400 flex items-center gap-1">
            <Calculator className="w-3 h-3" /> Rule 42 breakdown
          </div>
          <div className="text-muted-foreground">{t4b1.ii_rule_42.narration}</div>
          <div className="grid grid-cols-3 gap-2 mt-1">
            <div>Common ITC: <span className="font-mono">{inr(t4b1.ii_rule_42.total_common_itc)}</span></div>
            <div>D1 (exempt): <span className="font-mono text-amber-500">{inr(t4b1.ii_rule_42.d1)}</span></div>
            <div>D2 (5%): <span className="font-mono text-amber-500">{inr(t4b1.ii_rule_42.d2)}</span></div>
          </div>
        </div>
        {/* 4B(2) — temporary reversal */}
        <div className="grid grid-cols-3 gap-2 border-t border-white/10 pt-2 mt-2">
          <KPI label="4B(2) Only-in-2B IGST" value={inr(t4.b_reversal.b2_temporary_only_in_2b.igst)} tone="text-red-400" />
          <KPI label="4B(2) CGST" value={inr(t4.b_reversal.b2_temporary_only_in_2b.cgst)} tone="text-red-400" />
          <KPI label="4B(2) SGST" value={inr(t4.b_reversal.b2_temporary_only_in_2b.sgst)} tone="text-red-400" />
        </div>
      </div>

      {/* Table 4C · Net + Table 4D · Other details */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="card-sharp p-4 md:col-span-1">
          <div className="text-sm font-mono uppercase tracking-widest text-accent mb-2">Table 4C · Net ITC available</div>
          <div className="text-3xl font-bold font-mono tabular-nums text-emerald-500" data-testid="net-itc">{inr(t4.c_net_itc)}</div>
          <div className="text-xs text-muted-foreground mt-1">= 4A(5) − 4B(1) − 4B(2)</div>
        </div>
        <div className="card-sharp p-4 md:col-span-2">
          <div className="text-sm font-mono uppercase tracking-widest text-accent mb-2">Table 4D · Other details</div>
          <div className="grid grid-cols-2 gap-2">
            <KPI label="4D(1) Books matching earlier 2B (reclaimed)"
              value={inr(t4.d_other_details.d1_books_matching_earlier_2b_reclaimed.igst
                          + t4.d_other_details.d1_books_matching_earlier_2b_reclaimed.cgst
                          + t4.d_other_details.d1_books_matching_earlier_2b_reclaimed.sgst)} />
            <KPI label="4D(2) 2B flagged ineligible in 2B itself"
              value={inr(t4.d_other_details.d2_2b_flagged_ineligible.igst
                          + t4.d_other_details.d2_2b_flagged_ineligible.cgst
                          + t4.d_other_details.d2_2b_flagged_ineligible.sgst)} tone="text-amber-500" />
          </div>
        </div>
      </div>
    </div>
  );
}
